import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api } from '../api/client';
import type {
  CodeVerifyRequest,
  CodeVerifyResponse,
  LoginResponse,
  LoginTenantOption,
  MfaEnrollConfirmResponse,
  MfaEnrollResponse,
} from '../api/types';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { ErrorText } from './error-text';
import { Input } from './input';
import { Label } from './label';
import { LegalLinks } from './legal-links';
import { QrCode } from './qr-code';

/**
 * The one sign-in flow all three SPAs use (ADR-0023). Email ownership is proven
 * with a 6-digit EMAILED CODE — there are no emailed links (owner decision
 * 2026-10-05):
 *
 *   password ─┬─ ok ────────────────────────────┐
 *             ├─ several organizations → pick ──┤
 *             └─ email not confirmed → code ─┐  │
 *   "Email me a code" → code ────────────────┤  │
 *                         ├─ invitee → set password ─┤
 *                         └─ ok ──────────────────────┤
 *                                                     ├─ admin, no authenticator → set it up → recovery codes
 *                                                     ├─ authenticator → enter its code
 *                                                     └─ signed in
 *
 * Plain component state over the shared `api` client: one screen at a time,
 * each with its own form.
 */

type Step =
  | { kind: 'password' }
  | { kind: 'pickTenant'; via: 'password' | 'code'; tenants: LoginTenantOption[] }
  | { kind: 'requestCode' }
  | { kind: 'code'; notice: string }
  | { kind: 'setPassword'; reason: 'invite' | 'reset' }
  | { kind: 'mfa' }
  | { kind: 'enroll'; secret: string; uri: string }
  | { kind: 'recovery'; codes: string[] };

export interface SignInFlowProps {
  /** Card heading, e.g. "ClosingVault Admin". */
  title: string;
  /** The first screen's one-line purpose. */
  subtitle: string;
  /** Called once the session is complete (every MFA step done). */
  onSignedIn: (email: string) => void;
  /**
   * Open on the code screen for this address — e.g. right after a free-trial
   * signup, which has already emailed the founder a code to confirm the mailbox.
   */
  initialCode?: { email: string; notice: string };
  /**
   * Called once an emailed code has been accepted for this address (before any
   * authenticator step) — e.g. so a free-trial signup can report its confirmed account.
   */
  onEmailConfirmed?: (email: string) => void;
  /**
   * Optional content shown at the top of the first (password) screen — e.g. the
   * admin app's "New to ClosingVault?" sign-up link. Apps that are invite-only
   * pass nothing and render nothing new.
   */
  startSlot?: ReactNode;
  /**
   * Optional content shown under the "enter your code" screen's form. It must not
   * vary by whether the address has an account (the API never discloses that).
   */
  codeSlot?: ReactNode;
}

// Matches the API (src/modules/auth/passwords.ts).
const MIN_PASSWORD = 10;

export function SignInFlow({
  title,
  subtitle,
  onSignedIn,
  initialCode,
  onEmailConfirmed,
  startSlot,
  codeSlot,
}: SignInFlowProps) {
  const [step, setStep] = useState<Step>(
    initialCode ? { kind: 'code', notice: initialCode.notice } : { kind: 'password' },
  );
  const [email, setEmail] = useState(initialCode?.email ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const trimmedEmail = email.trim();

  function go(next: Step) {
    setError(null);
    setStep(next);
  }

  /** Runs one request with the busy flag + error capture. */
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** After a first factor (password or code): MFA steps, or done. */
  async function afterFirstFactor(res: LoginResponse, via: 'password' | 'code') {
    if (res.tenants && res.tenants.length > 0) {
      go({ kind: 'pickTenant', via, tenants: res.tenants });
    } else if (res.mfaEnrollmentRequired) {
      const enroll = await api.post<MfaEnrollResponse>('/auth/mfa/enroll', {});
      go({ kind: 'enroll', secret: enroll.secretBase32, uri: enroll.otpAuthUri });
    } else if (res.mfaRequired) {
      go({ kind: 'mfa' });
    } else {
      onSignedIn(trimmedEmail);
    }
  }

  function codeSentNotice(): string {
    return `If ${trimmedEmail} has a ClosingVault account, we have emailed it a 6-digit code.`;
  }

  async function login(chosenTenant?: string) {
    try {
      const res = await api.post<LoginResponse>('/auth/login', {
        email: trimmedEmail,
        password,
        ...(chosenTenant ? { tenantId: chosenTenant } : {}),
      });
      await afterFirstFactor(res, 'password');
    } catch (err) {
      if (err instanceof ApiError && err.problem?.code === 'email_unverified') {
        setTenantId(chosenTenant);
        setCode('');
        go({
          kind: 'code',
          notice: `Confirm your email address: we have emailed a 6-digit code to ${trimmedEmail}.`,
        });
        return;
      }
      throw err;
    }
  }

  async function verify(extra: Partial<CodeVerifyRequest> = {}) {
    const body: CodeVerifyRequest = {
      email: trimmedEmail,
      code: code.trim(),
      ...(tenantId ? { tenantId } : {}),
      ...extra,
    };
    const res = await api.post<CodeVerifyResponse>('/auth/code/verify', body);
    if (res.passwordRequired) {
      go({ kind: 'setPassword', reason: 'invite' });
      return;
    }
    if (!res.tenants?.length) onEmailConfirmed?.(trimmedEmail);
    await afterFirstFactor(res, 'code');
  }

  const onPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(() => login());
  };

  const onRequestCode = (e?: FormEvent) => {
    e?.preventDefault();
    void run(async () => {
      await api.post('/auth/code/request', { email: trimmedEmail });
      setCode('');
      setTenantId(undefined);
      go({ kind: 'code', notice: codeSentNotice() });
    });
  };

  const onCode = (e: FormEvent) => {
    e.preventDefault();
    void run(() => verify());
  };

  const onPick = (t: LoginTenantOption, via: 'password' | 'code') => {
    setTenantId(t.tenantId);
    void run(() =>
      via === 'password'
        ? login(t.tenantId)
        : verify({
            tenantId: t.tenantId,
            ...(newPassword ? { newPassword } : {}),
          }),
    );
  };

  const onSetPassword = (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    void run(() => verify({ newPassword }));
  };

  const onMfa = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.post('/auth/mfa/verify', { code: mfaCode.trim() });
      onSignedIn(trimmedEmail);
    });
  };

  const onEnrollConfirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<MfaEnrollConfirmResponse>('/auth/mfa/enroll/confirm', {
        code: mfaCode.trim(),
      });
      go({ kind: 'recovery', codes: res.recoveryCodes });
    });
  };

  const backToStart = (
    <Button
      type="button"
      variant="ghost"
      className="w-full"
      disabled={busy}
      onClick={() => {
        setTenantId(undefined);
        go({ kind: 'password' });
      }}
    >
      Back
    </Button>
  );

  let heading = subtitle;
  let body: ReactNode;
  switch (step.kind) {
    case 'password':
      body = (
        <form className="space-y-4" onSubmit={onPassword}>
          {startSlot}
          <EmailField value={email} onChange={setEmail} />
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => go({ kind: 'requestCode' })}
          >
            Email me a code
          </Button>
          <p className="text-xs text-muted-foreground">
            First time here, or forgot your password? Choose “Email me a code”.
          </p>
        </form>
      );
      break;

    case 'requestCode':
      heading = 'We will email you a 6-digit code.';
      body = (
        <form className="space-y-4" onSubmit={onRequestCode}>
          <EmailField value={email} onChange={setEmail} />
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a code'}
          </Button>
          {backToStart}
        </form>
      );
      break;

    case 'code':
      heading = 'Enter the 6-digit code we emailed you.';
      body = (
        <form className="space-y-4" onSubmit={onCode}>
          <p className="text-sm text-muted-foreground">{step.notice}</p>
          <div className="space-y-1.5">
            <Label htmlFor="email-code">6-digit code</Label>
            <Input
              id="email-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
          </div>
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => onRequestCode()}
          >
            Send a new code
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={busy || code.length !== 6}
            onClick={() => go({ kind: 'setPassword', reason: 'reset' })}
          >
            Set a new password with this code
          </Button>
          {backToStart}
          {codeSlot}
        </form>
      );
      break;

    case 'setPassword':
      heading =
        step.reason === 'invite' ? 'Choose a password for your account.' : 'Choose a new password.';
      body = (
        <form className="space-y-4" onSubmit={onSetPassword}>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">At least {MIN_PASSWORD} characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Saving…' : 'Set password and sign in'}
          </Button>
          {backToStart}
        </form>
      );
      break;

    case 'pickTenant':
      heading = 'Choose the organization to sign in to.';
      body = (
        <div className="space-y-2">
          {step.tenants.map((t) => (
            <Button
              key={t.tenantId}
              variant="outline"
              className="w-full justify-start"
              disabled={busy}
              onClick={() => onPick(t, step.via)}
            >
              {t.name}
            </Button>
          ))}
          <ErrorText error={error} />
          {backToStart}
        </div>
      );
      break;

    case 'mfa':
      heading = 'Enter the code from your authenticator app.';
      body = (
        <form className="space-y-4" onSubmit={onMfa}>
          <div className="space-y-1.5">
            <Label htmlFor="code">Authentication code</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Lost your device? Enter one of your recovery codes instead.
            </p>
          </div>
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Verifying…' : 'Verify'}
          </Button>
        </form>
      );
      break;

    case 'enroll':
      heading = 'Set up your authenticator.';
      body = (
        <form className="space-y-4" onSubmit={onEnrollConfirm}>
          <p className="text-sm text-muted-foreground">
            Admin accounts need two-step sign-in. Scan this code with an authenticator app (Google
            Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it
            shows.
          </p>
          <div className="flex justify-center">
            <QrCode value={step.uri} label="Authenticator set-up code" />
          </div>
          <p className="text-xs text-muted-foreground">
            Can’t scan? Enter this key instead:{' '}
            <code className="break-all font-mono" data-testid="totp-secret">
              {step.secret}
            </code>
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="code">Authenticator code</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              required
            />
          </div>
          <ErrorText error={error} />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Checking…' : 'Turn on two-step sign-in'}
          </Button>
        </form>
      );
      break;

    case 'recovery':
      heading = 'Save your recovery codes.';
      body = (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Each code signs you in once if you lose your authenticator. They are shown only now —
            store them somewhere safe.
          </p>
          <ul className="grid grid-cols-2 gap-1 rounded border p-3 font-mono text-sm">
            {step.codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button className="w-full" onClick={() => onSignedIn(trimmedEmail)}>
            I have saved these codes — continue
          </Button>
        </div>
      );
      break;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-sm space-y-4">
        <Card className="w-full">
          <CardHeader>
            <CardTitle as="h1">{title}</CardTitle>
            <p className="text-sm text-muted-foreground">{heading}</p>
          </CardHeader>
          <CardContent>{body}</CardContent>
        </Card>
        <LegalLinks />
      </div>
    </div>
  );
}

function EmailField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        autoComplete="username"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
    </div>
  );
}
